import { sortBy } from 'lodash';
import { Router } from '@angular/router';
import { ChangeDetectorRef, Component, Output, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import {
  LoyaltySettingsType,
  LoyaltySettingsPrelevelType,
  ReputationWithoutTargetType,
  WalletWithReputationDtoType,
} from '@sifca-monorepo/widget-generator';
import { MobileReputationService, MobileAuthService, MobileThemeService } from '@sifca-monorepo/clients';

import { reputationPicture } from '../../environments/environment';
import { of, Subject, switchMap, takeUntil } from 'rxjs';
import { intervalToDuration, isAfter, parseISO } from 'date-fns';

interface CountdownDisplay {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

interface LevelInactivityWarning {
  lossAmount: number;
  inactivityText: string;
}

interface ReputationLevelViewModel extends ReputationWithoutTargetType {
  description: string;
  formattedGoal: string;
  formattedInactivityLossAmount: string;
  formattedSegmentAmount: string;
  goal: number;
  inactivityWarning: LevelInactivityWarning | null;
  isActive: boolean;
  isUnlocked: boolean;
  progress: number;
  resolvedColor: string;
  rewards?: any | null;
  segmentAmount: number;
  title: string;
}

@Component({
  template: '',
})
export class MobileReputationComponent {
  @Output() countdownBlocksChange: EventEmitter<CountdownDisplay | undefined> = new EventEmitter<CountdownDisplay | undefined>();

  private readonly pointsFormatter = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  private destroy$ = new Subject<void>();
  private countdownInterval?: ReturnType<typeof setInterval>;

  loading = true;
  authenticated = true;
  isFinalLevel: boolean;
  countdownVisible = false;
  remainingToNextLevel = 0;
  countdown?: CountdownDisplay;
  currentLevelPercentage: number;
  dark$ = this.themeService.darkMode$;
  wallet?: WalletWithReputationDtoType;
  loyaltySettings: LoyaltySettingsType;
  reputationPicture = reputationPicture;
  preLevel: LoyaltySettingsPrelevelType;
  nextLevel: ReputationWithoutTargetType;
  currentLevel: ReputationWithoutTargetType;
  lang = localStorage.getItem('elvklang');
  reputationWallet: WalletWithReputationDtoType;
  reputationLevels: ReputationLevelViewModel[] = [];
  loyaltySettings$ = this.reputationService.loyaltySettings$;
  quantitativeWallet$ = this.reputationService.quantitativeWallet$;

  get countdownBlocks(): CountdownDisplay | undefined {
    return this.countdown;
  }

  get preLevelTitle(): string {
    return this.preLevelTranslation?.reputationLevel || this.preLevel?.name || 'Niveau de préparation';
  }

  get preLevelDescription(): string {
    return (
      this.preLevelTranslation?.description ||
      this.preLevel?.perks?.description ||
      'Participez à vos premières activités pour débloquer vos avantages.'
    );
  }

  get preLevelPictureUrl(): string | null {
    const picture = this.preLevel?.picture;
    if (picture?.baseUrl && picture?.path) {
      return `${picture.baseUrl}/${picture.path}`;
    }
    return null;
  }

  get preLevelBadgeStyles(): Record<string, string> {
    return {
      background: this.preLevel?.color,
      color: '#fff',
    };
  }

  get preLevelInactivityWarning(): { lossAmount: number; inactivityText: string } | null {
    if (!this.preLevel?.frequencyGoalEnabled) {
      return null;
    }
    return {
      lossAmount: this.preLevel?.lossAmount || 0,
      inactivityText: this.formatInactivityCycle(this.preLevel?.inactivityCycle),
    };
  }

  get activeLevelInactivityWarning(): { lossAmount: number; inactivityText: string } | null {
    if (this.currentLevel?.frequencyGoalEnabled) {
      return {
        lossAmount: this.currentLevel?.lossAmount || 0,
        inactivityText: this.formatInactivityCycle(this.currentLevel?.inactivityCycle),
      };
    }
    return this.preLevelInactivityWarning;
  }

  get showPreLevelAlert(): boolean {
    return !this.currentLevel && !!this.preLevelInactivityWarning;
  }

  get preLevelIsUnlocked(): boolean {
    return !!this.preLevel;
  }

  get hasCustomLoyaltyIcon(): boolean {
    return !!(this.loyaltySettings?.qualitative?.picture?.baseUrl && this.loyaltySettings?.qualitative?.picture?.path);
  }

  get loyaltyIconUrl(): string | null {
    const picture = this.loyaltySettings?.qualitative?.picture;
    if (picture?.baseUrl && picture?.path) {
      return `${picture.baseUrl}/${picture.path}`;
    }
    return null;
  }

  get loyaltyIconSrc(): string {
    return this.loyaltyIconUrl || this.reputationPicture;
  }

  private get preLevelTranslation(): any {
    return this.preLevel?.translation?.find((trs: any) => trs?.language?.code?.toLowerCase() === this.lang)?.content;
  }

  constructor(
    protected router: Router,
    protected cd: ChangeDetectorRef,
    protected translate: TranslateService,
    protected authService: MobileAuthService,
    protected themeService: MobileThemeService,
    protected reputationService: MobileReputationService,
  ) {}

  ngOnInit(): void {
    this.reputationService.selectedReputationWallet$.pipe(takeUntil(this.destroy$)).subscribe((wallet) => {
      this.wallet = wallet;
      this.preLevel = wallet?.prelevel;
      this.currentLevel = undefined;
      this.nextLevel = undefined;
      this.isFinalLevel = false;
      this.currentLevelPercentage = 0;
      this.remainingToNextLevel = 0;

      if (wallet) {
        const details = this.authService.getUserLevelDetails(wallet);
        if (details) {
          this.currentLevel = details.currentLevel;
          this.nextLevel = details.nextLevel;
          this.isFinalLevel = details.isFinalLevel;
          this.currentLevelPercentage = details.currentLevelPercentage;
          this.remainingToNextLevel =
            this.isFinalLevel || !details.nextLevel?.levelInterval?.max
              ? 0
              : Math.max(0, Math.round((details.nextLevel.levelInterval.max ?? 0) - +wallet.amount));
        }
      }

      const sortedLevels = wallet?.reputationLevels?.length ? (sortBy(wallet.reputationLevels, 'rank') as ReputationWithoutTargetType[]) : [];
      this.rebuildReputationLevels(sortedLevels);

      this.loading = false;
      this.cd.markForCheck();
    });

    this.reputationService.selectedSettings$
      .pipe(takeUntil(this.destroy$))
      .pipe(
        switchMap((settings) => {
          if (!settings) return of(null);
          this.loyaltySettings = settings;
          this.rebuildReputationLevels();
          if (!this.loyaltySettings?.target.pos?.aggregator) {
            this.authService.linkUserAccountForAggregator(this.loyaltySettings?.target?.pos?.id).pipe(
              switchMap((res) => {
                localStorage.setItem('userToken', res.accessToken);
                return this.reputationService.getCurrentUserReputationsLossDate();
              }),
            );
          } else {
            return this.reputationService.getCurrentUserReputationsLossDate();
          }
          return of(null);
        }),
      )
      .subscribe();

    this.reputationService.frequencyLossDate$.pipe(takeUntil(this.destroy$)).subscribe((lossDate) => {
      this.setupCountdown(lossDate?.lossDate);
    });
  }

  formatPoints(value?: number | null): string {
    if (value === null || value === undefined) {
      return '';
    }

    return this.pointsFormatter.format(value);
  }

  getLevelInactivityWarning(level?: ReputationWithoutTargetType | null): LevelInactivityWarning | null {
    if (!level?.frequencyGoalEnabled) {
      return null;
    }
    return {
      lossAmount: level?.lossAmount || 0,
      inactivityText: this.formatInactivityCycle(level?.inactivityCycle),
    };
  }

  isUnlockedLevel(level?: ReputationWithoutTargetType | null): boolean {
    if (!level) {
      return false;
    }
    if (this.isActiveLevel(level)) {
      return true;
    }
    const walletAmount = +(this.wallet?.amount ?? 0);
    const levelMin = +(level?.levelInterval?.min ?? 0);
    if (walletAmount > levelMin) {
      return true;
    }
    if (!this.currentLevel) {
      return false;
    }
    const currentRank = this.currentLevel?.rank ?? 0;
    const levelRank = level?.rank ?? 0;
    if (currentRank && levelRank) {
      return currentRank > levelRank;
    }
    const currentMax = this.currentLevel?.levelInterval?.max ?? 0;
    const levelMax = level?.levelInterval?.max ?? 0;
    return currentMax > levelMax;
  }

  getLevelColor(level?: ReputationWithoutTargetType | null): string {
    return level?.color || '#2563eb';
  }

  getLevelSegmentAmount(
    level: ReputationWithoutTargetType,
    index: number,
    levels: Array<Partial<ReputationWithoutTargetType>> = this.reputationLevels,
  ): number {
    if (!level?.levelInterval) return 0;
    const walletAmount = +(this.wallet?.amount ?? 0);
    const min = +(level.levelInterval.min ?? 0);
    const max = +(level.levelInterval.max ?? min);
    if (index === 0) {
      if (walletAmount >= max) return max;
      if (walletAmount <= min) return walletAmount;
      return walletAmount;
    }
    const prevMax = +(levels?.[index - 1]?.levelInterval?.max ?? min);
    if (walletAmount <= prevMax) return 0;
    if (walletAmount >= max) {
      return Math.max(0, max - prevMax);
    }
    return walletAmount - prevMax;
  }

  getLevelGoal(
    level: ReputationWithoutTargetType,
    index: number,
    levels: Array<Partial<ReputationWithoutTargetType>> = this.reputationLevels,
  ): number {
    if (!level?.levelInterval) return 0;
    const min = +(level.levelInterval.min ?? 0);
    const max = +(level.levelInterval.max ?? min);
    if (index === 0) {
      return max;
    }
    const prevMax = +(levels?.[index - 1]?.levelInterval?.max ?? min);
    return Math.max(0, max - prevMax);
  }

  protected rebuildReputationLevels(levels: ReputationWithoutTargetType[] = this.reputationLevels): void {
    this.reputationLevels = levels.map((level, index) => this.buildReputationLevelViewModel(level, index, levels));
  }

  protected extendReputationLevelViewModel(
    levelViewModel: ReputationLevelViewModel,
    _level: ReputationWithoutTargetType,
    _index: number,
    _levels: ReputationWithoutTargetType[],
  ): ReputationLevelViewModel {
    return levelViewModel;
  }

  private buildReputationLevelViewModel(
    level: ReputationWithoutTargetType,
    index: number,
    levels: ReputationWithoutTargetType[],
  ): ReputationLevelViewModel {
    const isActive = this.isActiveLevel(level);
    const isUnlocked = this.isUnlockedLevel(level);
    const resolvedColor = this.getLevelColor(level);
    const segmentAmount = this.getLevelSegmentAmount(level, index, levels);
    const goal = this.getLevelGoal(level, index, levels);
    const inactivityWarning = this.getLevelInactivityWarning(level);

    return this.extendReputationLevelViewModel(
      {
        ...level,
        description: this.getLevelDescription(level),
        formattedGoal: this.formatPoints(goal),
        formattedInactivityLossAmount: this.formatPoints(inactivityWarning?.lossAmount),
        formattedSegmentAmount: this.formatPoints(segmentAmount),
        goal,
        inactivityWarning,
        isActive,
        isUnlocked,
        progress: this.getLevelProgress(level),
        resolvedColor,
        segmentAmount,
        title: this.getLevelTitle(level),
      },
      level,
      index,
      levels,
    );
  }

  private formatInactivityCycle(value?: number | null): string {
    if (!value) {
      return this.translate.instant('FEW_HOURS');
    }
    if (value >= 24) {
      const days = Math.round(value / 24);
      const dayUnitKey = days > 1 ? 'DAY_UNIT_PLURAL' : 'DAY_UNIT';
      return `${days} ${this.translate.instant(dayUnitKey)}`;
    }
    const rounded = Math.round(value);
    const hourUnitKey = rounded > 1 ? 'HOUR_UNIT_PLURAL' : 'HOUR_UNIT';
    return `${rounded} ${this.translate.instant(hourUnitKey)}`;
  }

  trackByLevel(index: number, level: ReputationWithoutTargetType): string {
    return level?.id || level?.reputationLevel || `level-${index}`;
  }

  isActiveLevel(level: ReputationWithoutTargetType): boolean {
    if (!level || !this.currentLevel) return false;
    return level?.levelInterval?.max === this.currentLevel?.levelInterval?.max;
  }

  getLevelTitle(level?: ReputationWithoutTargetType): string {
    if (!level) return '';
    const translation = level.translation?.find((trs: any) => trs?.language?.code?.toLowerCase() === this.lang);
    return translation?.content?.reputationLevel || level.reputationLevel;
  }

  getLevelDescription(level?: ReputationWithoutTargetType): string {
    if (!level) return '';
    const translation = level.translation?.find((trs: any) => trs?.language?.code?.toLowerCase() === this.lang);
    return translation?.content?.description || level?.perks?.description || '';
  }

  getLevelProgress(level: ReputationWithoutTargetType): number {
    if (!level?.levelInterval || !this.wallet?.amount) return 0;
    const amount = +this.wallet.amount;
    const min = +level.levelInterval.min;
    const max = +level.levelInterval.max;
    if (amount <= min) {
      return 0;
    }
    if (amount >= max) {
      return 100;
    }
    return Math.min(100, Math.max(0, ((amount - min) / (max - min)) * 100));
  }

  private setupCountdown(lossDate?: string | null): void {
    if (!lossDate) {
      this.stopCountdown();
      this.countdownVisible = false;
      this.countdownBlocksChange.emit(undefined);
      this.cd.markForCheck();
      return;
    }
    const parsed = parseISO(lossDate);
    if (!isAfter(parsed, new Date())) {
      this.stopCountdown();
      this.countdownVisible = false;
      this.cd.markForCheck();
      return;
    }
    this.countdownVisible = true;
    this.stopCountdown();
    this.updateCountdown(lossDate);
    this.countdownInterval = setInterval(() => this.updateCountdown(lossDate), 1000);
  }

  private updateCountdown(lossDate: string): void {
    const target = parseISO(lossDate);
    const now = new Date();
    const diff = target.getTime() - now.getTime();
    if (diff <= 0) {
      this.countdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };
      this.stopCountdown();
      this.countdownVisible = false;
      this.countdownBlocksChange.emit(undefined);
      this.cd.markForCheck();
      return;
    }
    const duration = intervalToDuration({ start: now, end: target });
    this.countdown = {
      days: duration.days ?? 0,
      hours: duration.hours ?? 0,
      minutes: duration.minutes ?? 0,
      seconds: duration.seconds ?? 0,
    };
    this.countdownBlocksChange.emit(this.countdown);
    this.cd.markForCheck();
  }

  private stopCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = undefined;
    }
    this.countdownBlocksChange.emit(undefined);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopCountdown();
  }
}
